import { useMemo, useState, useRef, useEffect, useId, Fragment } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@api/v1/client'
import { requireArray } from '@api/v1/readShape'
import useReadState from '@hooks/useReadState'
import UsDateInput from '@components/common/UsDateInput'
import ControlCheckbox from '@components/common/ControlCheckbox'
import { ALL_DATES, DATE_RANGE_PRESET_GROUPS, dateFilterSummary, presetKeyFor, presetToFilterValue } from '@utils/cbDateSettings'
import { isAllSentinel, splitMultiValue, numberFilterValue, numberPickerOperator, NUMBER_PICKER_OPERATORS } from '@utils/reportFilterWire'
import { forgivingMatch, resolveFilterLabel } from './reportGridUtils'
import { accountOptionsQueryKey, fetchAccountOptions, descendantsOf, childrenIndex } from './accountPickerOptions'
import FilterPill from './FilterPill'
import { INLINE_FILTER_TYPES } from './inlineFilterTypes'
import { filterValueSummary, hasCriterion } from './filterValueSummary'
import RunQueueSplitButton from './queue/RunQueueSplitButton'

/**
 * Renders dynamic filter inputs for a CB Report.
 *
 * Filter value formats CB uses (server side parses these):
 *   DATE       — "YYYY-MM-DD|YYYY-MM-DD" (from|to) or "$$cb_all"
 *   SIMPLEDATE — "YYYY-MM-DD"
 *   BOOL       — "1" (Yes) | "0" (No) — always applied (CB-core parity, CC-49925)
 *   DROPDOWN — "key1|key2|..." (from filterConditions) or "$$cb_all"
 *   RADIO    — a single key (legacy renders radio buttons; CC-49861)
 *   LOOKUP/TAGMANAGER — "guid1|guid2|..." (object IDs; stored with ":name"
 *                        appended per id for display — stripped on the wire)
 *   NUMBER     — one bare number (spec 184; CB has no operator for it)
 *   NUMBERPICKER — "Operator|val1|val2" (e.g. "IsBetween|10|100"); the wire
 *                  sends CB's numeric operator code (spec 184)
 *   TEXT       — free text
 *   HIDDEN     — uses default, not rendered
 *   CHECKBOX/ACCOUNTSDROPDOWN — same as DROPDOWN
 */

const ALL = ALL_DATES

// CC-49321: `filterRelevant === false` marks a filter cb-api's per-report
// relevance config declared irrelevant for this report ("junk fields" that
// don't affect its stored proc). Hidden from the panel; its default value (if
// any) still flows to the server unchanged — see ReportView.buildInitialFilters.
const useFilterableColumns = (columns = []) =>
  useMemo(
    () =>
      [...columns]
        .filter(
          (c) =>
            c.filterable &&
            c.filterTypeName !== 'HIDDEN' &&
            c.filterTypeName !== 'NONE' &&
            c.filterRelevant !== false
        )
        .sort((a, b) => (a.filterOrder ?? 0) - (b.filterOrder ?? 0))
        // CC-50009: resolve the display label against the FULL column list before
        // the non-filterable siblings are dropped — that is where the friendly name
        // lives (report 303's filterable column is labelled "RespAtty" while its
        // display-only twin is labelled "Responsible Attorney"). Resolved once here
        // so every label, placeholder and aria-label below agrees.
        .map((c) => {
          const displayLabel = resolveFilterLabel(c, columns)
          return displayLabel === c.label ? c : { ...c, label: displayLabel, rawLabel: c.label }
        }),
    [columns]
  )

// spec 092: in pill mode the label lives on the pill's trigger, so rendering it
// here as well would put the same text on the page twice — visibly duplicated,
// and enough to make every `getByText(label)` in the suites ambiguous.
const fieldLabel = (col, hide = false) => (hide ? null : (
  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
    {col.label}
  </label>
))

/*
 * ADA sweep 2026-08-26 (Himaad B1/B2 + SC 1.4.11) — read this before adding a
 * control to this file.
 *
 * Every input and select below carried its own ad-hoc geometry:
 * `border border-gray-200 rounded-lg px-3 py-1.5` and a focus ring. Two things
 * were wrong with that. (1) #e5e7eb is 1.24:1 — on a white-on-white control the
 * 1px border is the ONLY thing identifying it as a control, so WCAG 2.1
 * SC 1.4.11 owes it 3.0:1; --color-rc-control (#8b95a5) is 3.03:1. (2) rounded-lg
 * is 8px, and B2 normalizes control radius to --radius-rc-control (6px) — 8px is
 * card/chip language, not button language.
 *
 * So: text inputs wear `.input-rc`, native selects wear `.select-rc`, and both
 * bring the border, the radius and the focus treatment with them.
 *
 * CRITICAL: those classes are UNLAYERED (src/index.css has no @layer; Tailwind
 * emits utilities inside `@layer utilities`, and an unlayered rule beats any
 * layered one regardless of specificity). A `px-3`, `py-1.5`, `rounded-lg`,
 * `text-sm` or `border-gray-200` left next to `.input-rc` is DEAD CODE that lies
 * to the next reader — which is why they are DELETED here rather than paired.
 * Same reason the old `focus:ring-2 focus:ring-cb-accent/20 focus:border-cb-accent`
 * is gone: `.input-rc`/`.select-rc` ring with the same 2px --color-rc-chip-text
 * outline every other rc control uses.
 *
 * THE EXCEPTION, and it is a real one: three controls in this file have an
 * absolutely-positioned sibling (a magnifier, a clear ×, a chevron, a calendar
 * button) whose only clearance is the input's own asymmetric padding — the
 * dropdown trigger, the lookup search box, and every UsDateInput. `.input-rc`
 * sets `padding: 0 0.5rem` unlayered, so it would silently eat their `pl-8` /
 * `pr-14` / `pr-7` and drop the text under the icon. Those three keep their
 * padding utilities and take the border and radius alone
 * (`border-rc-control rounded-rc-control`), which is all SC 1.4.11 and B2
 * actually ask for. Do not "finish the job" by putting `.input-rc` on them
 * without also solving the icon clearance.
 *
 * The two floating option panels below (DropdownFilter's and LookupFilter's
 * role="group" lists) keep `border-gray-200 rounded-lg` ON PURPOSE and were not
 * missed: that is a card outline around a floating panel, not the only
 * affordance identifying a control, so 1.24:1 is correct there and 8px is card
 * radius. Same rule, same wording, as the note at PopoverMenu.jsx:246-248.
 */
const TextFilter = ({ col, value, onChange, hideLabel }) => (
  <div>
    {fieldLabel(col, hideLabel)}
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={`Filter ${col.label}…`}
      aria-label={col.label}
      className="block w-full input-rc"
    />
  </div>
)

// CC-49925: boolean criteria are Yes/No — CB-core parity. Core renders a BOOL
// filter as a checkbox (Report.cshtml) whose makeFilters always posts
// "True"/"False"; there is no "don't filter" state in core, so V2 doesn't offer
// one either (Jordan 7/23). The value is always "1"/"0" (seeded by
// ReportView.buildInitialFilters); an unrecognized/empty value shows No.
// Supersedes the CC-49463 Yes/No/Both tri-state.
const BoolFilter = ({ col, value, onChange, hideLabel }) => (
  <div>
    {fieldLabel(col, hideLabel)}
    <select
      value={value === '1' ? '1' : '0'}
      onChange={(e) => onChange(e.target.value)}
      aria-label={col.label}
      className="block w-full select-rc"
    >
      <option value="1">Yes</option>
      <option value="0">No</option>
    </select>
  </div>
)

// CC-50013 G2: `col.omitAll` drops the All-Dates affordances (the preset option
// and the Clear button) for a report where an empty range does NOT mean "no date
// filter". Fee Receipts is the cautionary case: CB substitutes 1753-01-01 →
// today for null payment dates, so a cleared range produces an ALL-TIME run
// whose figures are WRONG rather than merely unfiltered (CC-50020). The
// specialized bars expressed this as DatePresetSelect's `omitAllDates`; carrying
// it here keeps that guarantee when a page moves onto this panel. Undefined on
// every generic report, so their behaviour is unchanged.
//
// `inline` (Section E2, Himaad 2026-08-24) un-stacks this control so it can sit
// in the pills row rather than inside a pill's popover. Layout only — every
// value this component puts on the wire is identical in both modes, and the
// tests pin the literals to prove it.
const DateFilter = ({ col, value, onChange, hideLabel, inline = false }) => {
  const omitAll = !!col.omitAll
  const isAll = value === ALL || !value
  const [from = '', to = ''] = isAll ? [] : (value || '').split('|')
  // THE WIRE. Byte-for-byte unchanged: $$cb_all when both ends are empty,
  // "from|to" otherwise. Nothing below rewrites it — `commit` gates ACCESS to
  // it, which is a different thing from changing it.
  const update = (newFrom, newTo) => {
    if (!newFrom && !newTo) onChange(ALL)
    else onChange(`${newFrom || ''}|${newTo || ''}`)
  }

  /*
   * THE omitAll GUARD (Section E2). This is the money half.
   *
   * `omitAll` only ever suppressed the ADVERTISED All-Dates affordances — the
   * comment at the head of this component says so explicitly: "Clearing both
   * inputs was already possible there; this just declines to advertise it."
   * Behind a closed pill that took a deliberate open plus two clears, so nobody
   * did it by accident. INLINE, both fields are always on screen and both-empty
   * is a two-backspace gesture.
   *
   * Measured on the CURRENT code before this change (frozen clock, omitAll
   * column seeded "2026-02-01|", from cleared): onChange fired with
   * "$$cb_all". That is the exact all-time run CC-50020 is about — on Fee
   * Receipts CB substitutes 1753-01-01 → today for null payment dates, so the
   * figures come back WRONG rather than merely unfiltered. Thirteen pages carry
   * omitAll (BillingRealization, Calendar, FeeAllocation, FeeReceipts,
   * GLBudget, GeneralLedger, MatterMetrics, PL, PaymentsByCase,
   * ReconciliationHistory, Status, TimeEntries, TimekeeperAnalysis) and they are
   * precisely the reports where an unbounded range is wrong.
   *
   * So the UI REFUSES instead of widening: no emit, and a visible, specific
   * message naming what is still applied. Modelled on ReportView's
   * `emptyDateFilters` (ReportView.jsx:160-175), which already blocks an empty
   * range client-side — but note that guard is on the GENERIC engine and
   * `omitAll` is set ONLY by the specialized `dateRangeField` factory
   * (specializedFilterFields.js:130-140), so it does not cover these pages.
   * Verified by reading both: the two guards are disjoint, not redundant.
   *
   * Deliberately NOT done: substituting a range of our own, and emitting "|"
   * (which reads as an empty range to the generic guard but decodes on the
   * specialized path to `{start:'', end:''}` — the same all-time run, just
   * spelled differently).
   */
  const [rangeRequired, setRangeRequired] = useState(false)
  // Clear a stale refusal when the value moves underneath us (Reset, an applied
  // saved view). Adjust-during-render rather than an effect — the same React
  // derived-state pattern UsDateInput.jsx:42-46 uses, no extra commit.
  const [lastValue, setLastValue] = useState(value)
  if (value !== lastValue) {
    setLastValue(value)
    if (rangeRequired) setRangeRequired(false)
  }
  const commit = (newFrom, newTo) => {
    if (omitAll && !newFrom && !newTo) {
      setRangeRequired(true)
      return
    }
    if (rangeRequired) setRangeRequired(false)
    update(newFrom, newTo)
  }
  // A preset is always a real range or $$cb_all, and $$cb_all is not offered at
  // all when omitAll — so picking one always resolves the refusal.
  const applyPreset = (key) => {
    const v = key && presetToFilterValue(key)
    if (!v) return
    if (rangeRequired) setRangeRequired(false)
    onChange(v)
  }
  // `col.omitPresets` (specializedFilterFields' dateRangeField): further preset keys a
  // report cannot run with — spec 214's Advanced Report range has no open end, so it
  // drops "Future" and "Today & Future". Absent on every other column, which keeps
  // exactly the list it had.
  const omitPresets = col.omitPresets
  const presetGroups = useMemo(() => {
    const dropped = Array.isArray(omitPresets) ? omitPresets : []
    if (!omitAll && dropped.length === 0) return DATE_RANGE_PRESET_GROUPS
    return DATE_RANGE_PRESET_GROUPS
      .map((g) => ({ ...g, presets: g.presets.filter((p) => !(omitAll && p.key === 'all') && !dropped.includes(p.key)) }))
      .filter((g) => g.presets.length > 0)
  }, [omitAll, omitPresets])
  // CC-49870: the select REFLECTS the active range — a chosen preset stays
  // selected instead of snapping back to "Quick range…", and editing the dates
  // away from any preset returns it to the placeholder. Derived (not stored):
  // whichever OFFERED preset resolves to exactly the current value is the
  // selection, so a range that equals an omitted preset reads as the placeholder.
  // The lookup is `presetKeyFor` (cbDateSettings.js), tested there directly; pass
  // it `presetGroups`, never DATE_RANGE_PRESET_GROUPS.
  const presetKey = useMemo(() => presetKeyFor(value, presetGroups), [value, presetGroups])

  /* CC-49325: quick presets — applying writes the literal resolved range
     into the from/to inputs below, so the user sees the real dates and
     can still customize them. */
  /*
   * ⚠️ `aria-label={`${col.label} preset`}` IS A LOAD-BEARING SELECTOR. Do not
   * reword it. Five unit locators use `getByLabelText('… preset')`, and
   * e2e/reports-grid-audit.spec.js:1119 counts
   * `select[aria-label$=" preset"]` as its ONLY inline-date coverage signal.
   * Renaming this label does NOT make that gate pass quietly — :1137 is
   * `expect(inlineDateCount, …).toBeGreaterThan(0)` and a zero count fails. What
   * it does is fail POST-DEPLOY with a message that blames the wrong thing: the
   * text reads "the date went back behind a pill", which sends the reader to the
   * inline-render code instead of to a renamed label. Hence the unit assertion
   * below, using the gate's selector byte-for-byte so the rename is caught here
   * first. (Corrected 2026-08-28: an earlier version of this comment claimed the
   * gate would "stop counting rather than fail loudly". It fails.)
   *
   * This select is the ONLY preset affordance. The Section E3 chip row that
   * briefly sat above it was removed 2026-08-28 (Jordan, in review) — see
   * specs/102 §4. It carries all 25 presets across 4 groups
   * (cbDateSettings.js:212-245), which stays the single preset vocabulary:
   * DateRangeFilter.jsx:5-14 builds its OWN 7-preset dayjs list, and copying it
   * here would silently change what "This Month" means on the wire.
   */
  const presetSelect = (
    <select
      value={presetKey}
      aria-label={`${col.label} preset`}
      onChange={(e) => applyPreset(e.target.value)}
      /* Inline drops `block w-full` and `mb-1.5` — those two utilities ARE the
         vertical stack. `.select-rc` alone is the shared 38px control, and it is
         unlayered, so no h-/px-/py-/rounded-/border- utility may be written
         beside it (it would be dead code that lies to the next reader). */
      className={inline ? 'select-rc' : 'block w-full select-rc mb-1.5'}
    >
      <option value="">Quick range…</option>
      {presetGroups.map((g) => (
        <optgroup key={g.label} label={g.label}>
          {g.presets.map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  )

  /* CC-49824: US-format inputs — native type="date" rendered the
     browser locale (DD/MM/YYYY off-US); V1 is always MM/DD/YYYY.

     NOT `.input-rc`: UsDateInput appends its own ` pr-7` to whatever
     className it is handed, to clear the calendar button it absolutely
     positions at right-1. `.input-rc` is unlayered and would override that
     padding to 8px, sliding "MM/DD/YYYY" under the calendar icon. Border and
     radius only, which is what SC 1.4.11 and B2 actually ask for.

     INLINE WIDTHS. `flex-1 min-w-0 w-full` is right inside a stacked panel and
     wrong inside the bar's `flex flex-wrap` row: UsDateInput wraps its input in
     a `relative inline-flex w-full` span (UsDateInput.jsx:67), so a percentage
     width resolves against a container that is itself being sized by its
     content. The pair therefore gets a DEFINITE container width inline, and the
     inputs a fixed basis inside it — either alone leaves the percentage
     indeterminate. 9rem/144px holds "MM/DD/YYYY" at text-sm (~88px) plus px-2.5
     (10px) and UsDateInput's pr-7 (28px) with room to spare. NOTE: measured on
     paper, not in a browser — worth an eyeball on a narrow viewport. */
  const rangeInputs = (
    <div className={inline ? 'flex items-center gap-2 w-[19.5rem]' : 'flex items-center gap-2'}>
      <UsDateInput
        value={from}
        onChange={(v) => commit(v, to)}
        aria-label={`${col.label} from`}
        className={`${inline ? 'w-36 shrink' : 'flex-1 min-w-0 w-full'} text-sm border border-rc-control rounded-rc-control px-2.5 py-1.5 focus:ring-2 focus:ring-cb-accent/20 focus:border-rc-chip-text`}
      />
      <span className="text-xs text-gray-500">to</span>
      <UsDateInput
        value={to}
        onChange={(v) => commit(from, v)}
        aria-label={`${col.label} to`}
        className={`${inline ? 'w-36 shrink' : 'flex-1 min-w-0 w-full'} text-sm border border-rc-control rounded-rc-control px-2.5 py-1.5 focus:ring-2 focus:ring-cb-accent/20 focus:border-rc-chip-text`}
      />
    </div>
  )

  const clearButton = !omitAll && (
    <button
      type="button"
      onClick={() => onChange(ALL)}
      className={`text-[10px] text-gray-500 hover:text-cb-accent-text${inline ? '' : ' mt-1'}`}
    >
      Clear / All Dates
    </button>
  )

  return (
    <div>
      {fieldLabel(col, hideLabel)}
      {inline ? (
        /* One row, wrapping. Everything above keeps its own geometry; only the
           axis changes, which is the whole of E2's layout half. */
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          {presetSelect}
          {rangeInputs}
          {clearButton}
        </div>
      ) : (
        <>
          {presetSelect}
          {rangeInputs}
          {clearButton}
        </>
      )}
      {/* The refusal. Visible, specific, AT the control, and it names what is
          still applied — because refusing leaves the wire holding the previous
          range while the field the user just emptied looks empty. Saying only
          "a range is required" would leave the user staring at two blank boxes
          with no way to know what the figures are actually scoped to. Colour is
          the repo's error red (text-red-700, as JobStatusCell.jsx:69 and
          ReportViewsMenu.jsx:694); role="alert" so it is announced rather than
          only seen (GeneralLedgerReport.jsx:263 does the same for its inverted
          range). */}
      {rangeRequired && (
        <p role="alert" className="mt-1 text-[11px] font-medium text-red-700">
          Both dates are empty. This report requires a range, so it was not cleared — still applied: {dateFilterSummary(value)}.
        </p>
      )}
    </div>
  )
}

// `inline` caps the width instead of filling: `block w-full` inside the bar's
// wrap row would stretch this single input across the whole line. The cap goes
// on the ROOT rather than on the input, because UsDateInput's own
// `relative inline-flex w-full` span (UsDateInput.jsx:67) needs a definite
// parent for its percentage to resolve against; one definite width at the top
// and percentages all the way down is the version that cannot go indeterminate.
//
// This control carries the five specialized as-of anchors (A/R, AP, Balance
// Sheet, Trial Balance and family) via specializedFilterFields.js:155-161
// `dateField`, plus every generic SIMPLEDATE column.
const SimpleDateFilter = ({ col, value, onChange, hideLabel, inline = false }) => (
  <div className={inline ? 'w-36' : undefined}>
    {fieldLabel(col, hideLabel)}
    {/* CC-49824: US-format input (see DateFilter — including why this one keeps
        its padding utilities instead of wearing `.input-rc`). */}
    <UsDateInput
      value={value || ''}
      onChange={onChange}
      aria-label={col.label}
      className="block w-full text-sm border border-rc-control rounded-rc-control px-3 py-1.5 focus:ring-2 focus:ring-cb-accent/20 focus:border-rc-chip-text"
    />
  </div>
)

// Click-outside helper for popover-style filters.
const useOutsideClick = (ref, onClose) => {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [ref, onClose])
}

// CC-49867: a report's own "All" condition duplicates the built-in All option,
// so it isn't rendered (bug #3) — but its KEY is remembered: the built-in All
// emits that key instead of the $$cb_all sentinel. That keeps V1's wire format
// for dropdowns that declare an explicit All (e.g. Trust Balance's 0=Has
// Balance / 1=All), whose procs int-parse the value and would crash on
// "$$cb_all" (the CC-49871 SQL conversion error).
const splitAllCondition = (col) => {
  const all = (col.filterConditions || []).find(
    (c) => String(c.value ?? '').trim().toLowerCase() === 'all'
  )
  return {
    staticConditions: (col.filterConditions || []).filter((c) => c !== all),
    allKey: all ? String(all.key) : ALL,
  }
}

// Dropdowns whose option list V1 loads dynamically rather than from the
// definition (CC-49864/65/68): CB ships them with EMPTY filterConditions, so
// V2 showed only "All". User-type lists are recognized by label.
const USERISH_LABEL_RE = /(timekeeper|attorney|lawyer|originator|biller|paralegal|user)/i

const DropdownFilter = ({ col, value, onChange, accountIds, hideLabel }) => {
  // Stable id for aria-controls on the trigger (WCAG 4.1.2).
  const listId = useId()
  const { staticConditions, allKey } = splitAllCondition(col)
  // CC-49864/65: ACCOUNTSDROPDOWN is ALWAYS dynamic — V1 renders an empty
  // <select> and fills it from cbGetAccountsLite restricted to the definition's
  // FilteredAccountIds (grouped by account type, inactive included on purpose:
  // they may appear in the data). CC-49868: user-type dropdowns (Responsible
  // Attorney / Timekeeper) arrive with empty conditions and load the active
  // user list; option keys are user object ids — the wire format the procs
  // filter on.
  const isAccounts = col.filterTypeName === 'ACCOUNTSDROPDOWN'
  const isUserList = !isAccounts && staticConditions.length === 0 && USERISH_LABEL_RE.test(col.label || '')
  // Whether this dropdown's options come from the SERVER at all. Every read-state
  // branch below is gated on it: a definition-driven dropdown's query is
  // `enabled: false` for the life of the component, so its read state is
  // permanently 'idle' and says nothing about anything.
  const isDynamic = isAccounts || isUserList
  // CC-53890: picking a parent account also picks its sub-accounts (V1's tree).
  // Opt-in per column — only the General Ledger's picker, where it is measured:
  // CB matches account ids EXACTLY there, so a parent alone would silently leave
  // out every sub-account. A generic report's account filter has its own proc
  // semantics, never measured for this, so it keeps one-box-one-id.
  const selectSubAccounts = isAccounts && !!col.selectSubAccounts
  const dynQuery = useQuery({
    queryKey: isAccounts
      ? accountOptionsQueryKey(accountIds)
      : ['report-filter-users'],
    queryFn: async () => {
      // CC-53890: one option builder (labels that tell duplicates apart, search
      // over number + name + type, the parent links) shared with the GL page,
      // so the name a user ticks is the name its export prints. requireArray
      // lives inside it — see the read-state note below.
      if (isAccounts) return fetchAccountOptions(accountIds)
      const resp = await api.get('/users')
      return requireArray(resp?.data, { noun: 'user list', entryNoun: 'user' })
        .map((u) => ({ key: String(u.id), value: u.name || u.displayName || '' }))
        .filter((o) => o.value)
        .sort((a, b) => a.value.localeCompare(b.value))
    },
    enabled: isDynamic,
    staleTime: 60_000,
  })
  /*
   * CC-53132 — three outcomes, not two (see `@api/v1/readState`). This read used
   * `isLoading`, which is `isPending && isFetching` and therefore FALSE while
   * react-query has PAUSED the fetch (default networkMode 'online' with
   * `onlineManager` believing the browser offline): `queryFn` is never called,
   * nothing throws, nothing logs, `data` stays undefined. The dropdown then
   * opened showing its built-in "All" row and nothing else — i.e. "this firm has
   * no bank accounts / no timekeepers you can pick" — with no spinner and no
   * error to contradict it.
   *
   * On this control that is a money defect, not a cosmetic one. An EMPTY account
   * selection means ALL accounts to CB, so a silently empty option list leaves an
   * accounting report running firm-wide while the trigger reads "None selected"
   * and the user has no way to narrow it.
   */
  const dynState = useReadState(isAccounts ? 'report-filter-accounts' : 'report-filter-users', dynQuery)
  const dynFailed = isDynamic && dynState === 'failed'
  const dynBlocked = isDynamic && dynState === 'blocked'
  // 'idle' rides with 'loading' only while the query is enabled, where it can
  // only be the frame before the fetch starts. The permanent 'idle' — a
  // definition-driven dropdown — is excluded by `isDynamic`, or every static
  // dropdown in the product would sit under "Loading options…" forever.
  const dynLoading = isDynamic && (dynState === 'loading' || dynState === 'idle')
  // The `|| []` survives only because the derivations below need something to
  // walk. It is no longer load-bearing: what an empty list MEANS is now decided
  // by `dynState`, not by this array's length.
  const rawConditions = isDynamic ? dynQuery.data || [] : staticConditions
  // CC-50031: never render the same option twice. Two checkboxes sharing a key
  // are indistinguishable to the user AND behave as one control (checking
  // either checks both, because selection is tracked by key) — which is exactly
  // how this was reported: "Real Estate" appearing twice on report 143.
  //
  // The duplicate here is dirty TENANT DATA, not something we inject: CB's
  // Crm_GetSearchDropDownValues(1001, 1054) returns 23 values for vladdemo
  // including two byte-identical {Value:'Real Estate', DisplayText:'Real Estate'}
  // entries (captured live 2026-08-10). Cleaning that up is a data task. This
  // guard is still correct on its own terms — a duplicate option can never
  // change the filter result, since both entries post the same value — so the
  // UI should collapse them whatever the upstream source (definition,
  // hydration, /users or /accounting/accounts). First occurrence wins, which
  // preserves the server's ordering.
  const conditions = (() => {
    const seen = new Set()
    return rawConditions.filter((c) => {
      const k = String(c?.key)
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
  })()

  // CC-49896 S1: an EMPTY value is "nothing selected" (All deselectable — the
  // run request omits the filter, V1's uncheck-all behavior), distinct from the
  // All sentinel / declared All key. CC-49897: CB stores multi-value DEFAULTS
  // comma-separated ("1,2") — splitMultiValue parses both commas and pipes so
  // definition defaults check the right boxes instead of one bogus "1,2" token.
  const isAll = (!!value && isAllSentinel(value)) || (allKey !== ALL && String(value) === allKey)
  const selected = isAll || !value ? [] : splitMultiValue(value).filter((p) => !isAllSentinel(p))
  const [open, setOpen] = useState(false)
  // CC-49990: typeahead over a long pick-list (V1 has one; cb2 didn't).
  const [query, setQuery] = useState('')
  const wrapperRef = useRef(null)
  useOutsideClick(wrapperRef, () => { setOpen(false); setQuery('') })

  // Accounts render grouped by account type (V1 parity); flat lists don't.
  // Plain derivation (no memo): the list tops out at a tenant's account/user
  // count, and `conditions` changes identity per render anyway.
  const optionGroups = (() => {
    if (!conditions.some((c) => c.group)) return [[null, conditions]]
    const map = new Map()
    for (const c of conditions) {
      const g = c.group || 'Other'
      if (!map.has(g)) map.set(g, [])
      map.get(g).push(c)
    }
    return [...map.entries()]
  })()

  // CC-49990: offer the search box once the list is long enough to be worth
  // filtering. 8 is the point where scanning stops being instant; below it the
  // input is pure chrome. Applies to every long dropdown, not just Timekeeper.
  const SEARCH_THRESHOLD = 8
  const showSearch = conditions.length >= SEARCH_THRESHOLD
  const searching = showSearch && !!query.trim()
  // CC-53890: an option may carry its own `search` text — the account picker's
  // is number + name + type group, so "1000", "morgan" and "bank" all find JP
  // Morgan Operating. Every other dropdown has none and matches its label, as
  // before.
  const visibleGroups = searching
    ? optionGroups
        .map(([g, opts]) => [g, opts.filter((c) => forgivingMatch(c.search ?? c.value, query))])
        .filter(([, opts]) => opts.length > 0 || optionGroups.length === 1)
    : optionGroups
  // CC-49990 (re-fix): the built-in All row is rendered OUTSIDE the option list,
  // so the first pass left it visible under every query — searching "PHH"
  // returned PHH *and* All, which is what QA re-failed. Put All through the same
  // forgiving test as the options: it stays while browsing, and while searching
  // only when the query actually matches it.
  const showAllOption = !searching || forgivingMatch('All', query)

  // CC-49826: mutually exclusive option sets render as a SINGLE-select — the
  // legacy "Invoice Posted" filter ships as a dropdown whose options
  // (Posted / Unposted) are mutually exclusive, so multi-checking them is
  // meaningless. (RADIO itself now maps to RadioFilter — CC-49861.) The All
  // option emits the declared All key when the report has one (CC-49867).
  const optionText = conditions.map((c) => String(c.value ?? '').trim().toLowerCase())
  const singleSelect =
    col.filterTypeName === 'RADIO' ||
    (optionText.includes('posted') && optionText.includes('unposted'))
  if (singleSelect) {
    return (
      <div>
        {fieldLabel(col, hideLabel)}
        <select
          aria-label={col.label}
          value={isAll ? allKey : selected[0] ?? allKey}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full select-rc"
        >
          <option value={allKey}>All</option>
          {conditions.map((c) => (
            <option key={c.key} value={String(c.key)}>{c.value}</option>
          ))}
        </select>
      </div>
    )
  }

  const childIndex = selectSubAccounts ? childrenIndex(conditions) : null
  const toggle = (key) => {
    if (key === ALL) {
      // CC-49896 S1 / CC-49867: All is DESELECTABLE — unchecking it clears the
      // selection ('' → the run request omits the filter; the report falls
      // back to its server default, exactly V1's uncheck-all).
      onChange(isAll ? '' : allKey)
      return
    }
    // CC-53890: on the GL picker a parent brings its whole family — the parent's
    // OWN id too, because a parent account carries postings of its own (measured:
    // 117 Fees Received alone returns its own entries). Unticking the parent
    // removes the family; a sub-account still toggles on its own.
    const family = selectSubAccounts ? [key, ...descendantsOf(key, conditions, childIndex)] : [key]
    const next = selected.includes(key)
      ? selected.filter((k) => !family.includes(k))
      : [...selected, ...family.filter((k) => !selected.includes(k))]
    onChange(next.join('|'))
  }
  // Only computed where it is shown (the GL picker), so no other dropdown pays for
  // it. The parent → children index is built ONCE per render and shared by every
  // row's count, so a large chart costs one pass plus each row's own subtree —
  // not a full rescan of the chart per row while the user types in search.
  const subAccountCount = selectSubAccounts
    ? (k) => descendantsOf(k, conditions, childIndex).length
    : () => 0

  // CC-49896 S2: multi-selected bank accounts read as a COUNT (the V1 ask —
  // names overflow the trigger); other dropdowns keep the readable names.
  //
  // CC-49867: the label must state the ACTUAL selection state. It used to render
  // "All" for an empty selection too, on the reasoning that the run omits the
  // filter either way — but that made two distinct states look identical: QA
  // unchecked All and the trigger still read "All", so there was no way to tell
  // whether the box was checked. The wire behavior is unchanged (empty still
  // omits the filter and the report falls back to its server default); only the
  // label now distinguishes "All is checked" from "nothing is checked".
  const summary = isAll
    ? 'All'
    : selected.length === 0
      ? 'None selected'
      : isAccounts && selected.length > 1
        ? `${selected.length} accounts selected`
        : selected
            .map((k) => conditions.find((c) => String(c.key) === String(k))?.value || k)
            .join(', ')

  return (
    <div ref={wrapperRef} className="relative">
      {fieldLabel(col, hideLabel)}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={col.label}
          /*
           * WCAG 4.1.2. This looked like a plain button to assistive tech: no
           * popup declared and, more importantly, no expanded state, so a screen
           * reader could not tell an open list from a closed one.
           *
           * aria-haspopup="true", NOT "listbox": the panel below holds a search
           * TEXTBOX and grouped CHECKBOXES. A listbox may not contain either, so
           * declaring one would be a worse lie than declaring nothing — and
           * role="option" without roving arrow keys promises keyboard semantics
           * this control does not implement.
           */
          aria-haspopup="true"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          /* Not `.btn-rc`: the clear × and the chevron are absolutely positioned
             over this trigger, and their only clearance is the pr-14/pr-8 below.
             `.btn-rc` is unlayered and sets its own symmetric padding, so it
             would put the summary text under the icons. Border + radius only —
             #e5e7eb (1.24:1) -> --color-rc-control (3.03:1), 8px -> 6px. */
          className={`block w-full text-left text-sm border border-rc-control rounded-rc-control py-1.5 pl-3 hover:bg-gray-50 focus:ring-2 focus:ring-cb-accent/20 focus:border-rc-chip-text ${selected.length > 0 ? 'pr-14' : 'pr-8'}`}
        >
          <span className="block truncate">{summary}</span>
        </button>
        {/* CC-49896 S2: one × clears every selected value (V1's clear affordance;
            sits left of the chevron, same layout as ObjectLookup). */}
        {selected.length > 0 && (
          <button
            type="button"
            // CC-53890: a column may say what "cleared" means. On the GL picker an
            // empty value is "None selected", which REFUSES to run — so clearing
            // there returns to All instead of stranding the user in a refusal.
            onClick={() => onChange(col.clearValue ?? '')}
            aria-label={`Clear ${col.label}`}
            className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <span aria-hidden="true" className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </div>
      {open && (
        /* z-50: must paint over the report grid's sticky header (z-20), which
           otherwise cuts across the open list (CC-49862). */
        <div
          id={listId}
          role="group"
          aria-label={`${col.label} options`}
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {/* CC-49990: V1's long pick-lists are searchable; cb2 rendered a plain
              scrolling list, so finding one timekeeper among dozens meant hunting.
              The box appears once the list is long enough to be worth filtering —
              on a short list it would just be chrome. Matching is the same
              forgiving comparison the grid's column filters use.

              A3 (Himaad, 8/26): this is the THIRD implementation of "search this
              column's values" and it had a third geometry — 30px, rounded-md,
              border-gray-200 at 1.24:1. It is `.input-rc` now, the same class the
              grid's per-column "Filter..." boxes wear, so the three finally agree
              on height, radius and a 3.03:1 border. The old h/text/px/py/rounded/
              border utilities are DELETED, not kept alongside: `.input-rc` is
              unlayered, so they were about to become dead code. */}
          {showSearch && (
            <div className="sticky top-0 bg-white border-b border-gray-100 p-2">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={`Search ${col.label}`}
                placeholder={`Search ${col.label.toLowerCase()}…`}
                className="block w-full input-rc"
              />
            </div>
          )}
          {/* CC-53890: a column-supplied note on how the picker behaves (the GL
              picker's parent/sub-account and Account Types rules). gray-600 on
              white is 7.56:1. */}
          {col.help && <p className="px-3 pt-2 pb-1 text-xs text-gray-600">{col.help}</p>}
          {showAllOption && (
            <label className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-gray-50 text-sm cursor-pointer">
              {/* B4 (spec 117). This one had NO className at all — a bare browser
                  checkbox, in the dropdown filter every report's Bar 2 opens. The
                  row already had its hover; only the control was out of step. */}
              <ControlCheckbox checked={isAll} onChange={() => toggle(ALL)} />
              <span className="font-medium">All</span>
            </label>
          )}
          {dynLoading && <div className="px-3 py-2 text-xs text-gray-500">Loading options…</div>}
          {dynFailed && (
            /* CC-49322 AC: a list that can't load says so instead of silently
               showing only "All". */
            <div className="px-3 py-2 text-xs text-red-600">Couldn’t load the list — reopen to retry.</div>
          )}
          {dynBlocked && (
            /* CC-53132: the paused read has no error to show and no spinner
               running, so without this line an unreachable server is
               indistinguishable from a firm that owns no accounts. Retry lives
               HERE rather than on the page: this popover closes on any outside
               click, so sending the user elsewhere loses their place. */
            <div role="alert" className="border-b border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <p className="font-medium">Can’t reach the server, so these options never loaded.</p>
              <p className="mt-0.5 text-amber-800">This is not an empty list — check your connection.</p>
              <button
                type="button"
                onClick={() => dynQuery.refetch()}
                className="mt-1 font-medium text-cb-accent-text underline hover:no-underline"
              >
                Try again
              </button>
            </div>
          )}
          {visibleGroups.map(([group, opts]) => (
            <Fragment key={group ?? '(flat)'}>
              {group && (
                <div className="px-3 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {group}
                </div>
              )}
              {opts.map((c) => {
                const subs = subAccountCount(String(c.key))
                return (
                  <label
                    key={c.key}
                    className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-gray-50 text-sm cursor-pointer"
                    // CC-53890: every account picker shows the chart's hierarchy (V1's
                    // tree): the shared option builder lists each sub-account after
                    // its parent, so the indent is what makes that order legible on
                    // all 13 account-filtered reports, not just the GL. Display only —
                    // whether ticking a parent brings its family stays the GL's
                    // opt-in (`selectSubAccounts`), and only there does the row say
                    // "+ N sub-accounts". Only account options carry `depth`. Inline,
                    // so it wins over px-3's left padding without a utility pair.
                    style={c.depth ? { paddingLeft: `${0.75 + c.depth}rem` } : undefined}
                  >
                    {/* B4 (spec 117) — see the "All" row above. */}
                    <ControlCheckbox
                      checked={!isAll && selected.includes(String(c.key))}
                      onChange={() => toggle(String(c.key))}
                    />
                    <span className="truncate">{c.value}</span>
                    {/* Part of the checkbox's accessible name on purpose: a screen
                        reader hears that ticking this box ticks more than one.
                        gray-500 is 4.84:1 on white, 4.63:1 on the gray-50 hover
                        (Tailwind v4 palette, computed). The {' '} separates the
                        two words in that name; a flex row does not render it. */}
                    {subs > 0 && ' '}
                    {subs > 0 && (
                      <span className="ml-auto flex-none text-[11px] text-gray-500">
                        {`+ ${subs} sub-account${subs === 1 ? '' : 's'}`}
                      </span>
                    )}
                  </label>
                )
              })}
            </Fragment>
          ))}
          {searching && !showAllOption && visibleGroups.every(([, o]) => o.length === 0) && (
            <div className="px-3 py-2 text-xs text-gray-500">No matches for “{query}”.</div>
          )}
        </div>
      )}
    </div>
  )
}

// CC-49861: RADIO filters are SINGLE-select — legacy renders them as radio
// buttons, so exactly one option is ever active and the wire value is one key.
// The multi-select DropdownFilter let users check e.g. Billed AND Collected,
// which the proc can't honor. A radio also always HAS a value (no "All"),
// unless the report itself declares an All condition — then it's offered and
// emits that condition's own key.
const RadioFilter = ({ col, value, onChange, hideLabel }) => {
  const { staticConditions, allKey } = splitAllCondition(col)
  const hasDeclaredAll = allKey !== ALL
  // splitMultiValue: a stale multi-select-era value ("1|2") or a CB comma
  // default ("1,2" — CC-49897) collapses to its first key; a radio is one key.
  const current = splitMultiValue(value)[0] || ''
  const known =
    (hasDeclaredAll && current === allKey) ||
    staticConditions.some((c) => String(c.key) === current)
  return (
    <div>
      {fieldLabel(col, hideLabel)}
      <select
        value={known ? current : ''}
        onChange={(e) => onChange(e.target.value)}
        aria-label={col.label}
        className="block w-full select-rc"
      >
        {/* Placeholder only surfaces for a value we can't map (e.g. a stale
            persisted "$$cb_all" from the multi-select era) — the run request
            omits it and the proc applies the report default. */}
        {!known && <option value="">Report default</option>}
        {hasDeclaredAll && <option value={allKey}>All</option>}
        {staticConditions.map((c) => (
          <option key={c.key} value={String(c.key)}>{c.value}</option>
        ))}
      </select>
    </div>
  )
}

/**
 * For LOOKUP/TAGMANAGER filters CB stores the target object type in
 * filterConditions[0].key (matches Report.cshtml line 430).
 */
const lookupTypeId = (col) => {
  const fromConditions = col.filterConditions?.[0]?.key
  if (fromConditions) return parseInt(fromConditions, 10)
  // Fallback: parse from field id "typeId:fieldId"
  if (col.field) return parseInt(String(col.field).split(':')[0], 10)
  return null
}

/**
 * Parse default value formats from server. Single LOOKUP defaults look like
 * "id:displayName"; multi-value (TAGMANAGER) like "id1:n1|id2:n2|...".
 * User selections are stored the same way (CC-49866) so the label survives a
 * remount; split on the FIRST colon only — display names can contain colons.
 */
const parseDefault = (value) => {
  if (!value || value === ALL) return []
  const seen = new Set()
  return value.split('|').filter(Boolean).reduce((acc, part) => {
    const idx = part.indexOf(':')
    const entry = idx === -1
      ? { id: part, name: '' }
      : { id: part.slice(0, idx), name: part.slice(idx + 1) }
    // CC-49947: a repeated id (stale stored value / server default) must not
    // become two identical chips — same React key, and a duplicate on the wire.
    const key = String(entry.id)
    if (seen.has(key)) return acc
    seen.add(key)
    acc.push(entry)
    return acc
  }, [])
}

const LookupFilter = ({ col, value, onChange, multi = false, hideLabel }) => {
  const lookupListId = useId()
  const typeId = lookupTypeId(col)
  const initial = parseDefault(value)
  const [selected, setSelected] = useState(initial)
  const [query, setQuery] = useState(multi ? '' : (initial[0]?.name || ''))
  const [open, setOpen] = useState(false)
  // CC-49747: the dropdown arrow browses the full value list (V1 combo parity);
  // typing switches back to search mode.
  const [browsing, setBrowsing] = useState(false)
  const wrapperRef = useRef(null)
  const lastEmitted = useRef(null)
  useOutsideClick(wrapperRef, () => { setOpen(false); setBrowsing(false) })

  // Re-sync ONLY on EXTERNAL value changes (e.g. Reset). Skip our own emit —
  // re-parsing what we just wrote would churn state for nothing, and any code
  // path that still holds an ids-only value (pre-CC-49866 persisted filters)
  // would wipe the selected label.
  useEffect(() => {
    if (value === lastEmitted.current) return
    const next = parseDefault(value)
    setSelected(next)
    if (!multi) setQuery(next[0]?.name || '')
  }, [value, multi])

  const trimmed = query.trim()
  const searching = trimmed.length >= 2
  const listOpen = open && (browsing || searching)
  const enabled = !!typeId && listOpen
  // CC-49853: a "Client…" lookup against the Contacts type (3) lists CLIENTS,
  // not every contact (clientsOnly scopes /objects/3 to isClient). An earlier
  // attempt proxied V1's Crm2_AutoComplete via a new /autocomplete endpoint for
  // exact V1 parity, but that route wasn't reachable on the deployed cb-api
  // worker — reverted to this working /objects path per Jordan.
  const clientsOnly = typeId === 3 && /client/i.test(col.label || '')
  // `lookup=true` marks this a value PICKER for cb-api: active-only scoping plus
  // a deterministic (prefix-first, then alphabetical) order. CB's /item/exists
  // otherwise defaults to active-status Both and returns an arbitrary page —
  // which is what QA saw as "incorrect and extra values" (CC-49853).
  const lookupQuery = useQuery({
    queryKey: ['report-lookup', typeId, clientsOnly, browsing ? '(all)' : trimmed],
    queryFn: async () => {
      const resp = await api.get(`/objects/${typeId}`, {
        q: browsing ? '' : trimmed,
        limit: browsing ? 50 : 15,
        lookup: 'true',
        ...(clientsOnly ? { clientsOnly: 'true' } : {}),
      })
      // CC-50289: keep `truncated`/`total` — a shortened list that looks complete
      // is how "my client isn't in the picker" happens. Same reason `resp.data` is
      // asserted rather than `|| []`-ed: a body we can't read must not arrive at
      // the list as "no matches".
      return {
        items: requireArray(resp?.data, { noun: 'value list', entryNoun: 'value' }),
        truncated: !!resp?.meta?.truncated,
        total: resp?.meta?.total ?? null,
      }
    },
    enabled,
    staleTime: 30_000,
  })
  const { data: result } = lookupQuery
  /*
   * CC-53132 — three outcomes, not two (see `@api/v1/readState`). `isLoading` is
   * false for a PAUSED fetch, so this picker rendered "No matches" for a search
   * it never sent: no request, no error, no spinner. That is the documented "my
   * client isn't in the picker" symptom, and CC-50289's truncation notice already
   * exists for the milder version of the same lie. On a client/matter picker the
   * consequence is a report run against the wrong scope — or an invoice hunt
   * abandoned because the client looked absent.
   *
   * 'idle' rides with 'loading': this list renders only while `listOpen`, and
   * `enabled` is `!!typeId && listOpen` with a missing/non-numeric typeId already
   * returned above as the advanced text input — so whenever this markup is on
   * screen the query is enabled, and 'idle' can only be the frame before the
   * fetch starts. It must not fall through to "No matches".
   */
  const lookupState = useReadState(`report-lookup:${typeId}`, lookupQuery)
  const searchReady = lookupState === 'ready'
  const searchPending = lookupState === 'loading' || lookupState === 'idle'
  const items = result?.items || []

  const emit = (next) => {
    // CC-49947: dedupe by id on the way out as a belt-and-braces guard, so a
    // duplicate can never reach the wire even if one slips into `selected`
    // (e.g. a server default that repeats an id).
    const seen = new Set()
    const unique = next.filter((s) => {
      const k = String(s.id)
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
    // CC-49866: keep the display name in the stored value ("id:name" — CB's own
    // filterDefault format) so the label survives the panel unmounting on Run
    // and cross-session filter persistence. filtersAsArray strips values back
    // to ids-only on the wire; '|' is the multi-value separator, so it can't
    // appear inside a name.
    const v = unique.length === 0
      ? ''
      : unique.map((s) => (s.name ? `${s.id}:${String(s.name).replace(/\|/g, ' ')}` : s.id)).join('|')
    lastEmitted.current = v
    onChange(v)
  }

  // CC-49947: an already-picked value is not selectable again. Previously the
  // click was silently swallowed (`select` deduped but said nothing), so on
  // Aged AP Detail a vendor looked selectable twice with no feedback.
  const isSelected = (id) => selected.some((s) => String(s.id) === String(id))

  const select = (item) => {
    if (multi) {
      if (isSelected(item.id)) return
      const next = [...selected, { id: item.id, name: item.name }]
      setSelected(next)
      setQuery('')
      emit(next)
    } else {
      const next = [{ id: item.id, name: item.name }]
      setSelected(next)
      setQuery(item.name || '')
      setOpen(false)
      setBrowsing(false)
      emit(next)
    }
  }

  const remove = (id) => {
    const next = selected.filter((s) => s.id !== id)
    setSelected(next)
    emit(next)
  }

  const clearSingle = () => {
    setSelected([])
    setQuery('')
    onChange('')
  }

  if (!typeId) {
    return (
      <div>
        {fieldLabel(col, hideLabel)}
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Object IDs (advanced)"
          aria-label={col.label}
          className="block w-full input-rc"
        />
      </div>
    )
  }

  return (
    <div ref={wrapperRef} className="relative">
      {fieldLabel(col, hideLabel)}
      {/* CC-49862 (Jordan, 7/22): the selected-value chips render BELOW the
          search box, not above it. Above-the-box chips pushed the input down
          and knocked it out of vertical alignment with the sibling filter
          fields in the row; keeping the input on top holds that alignment. */}
      <div className="relative">
        {/* CC-49862: the magnifier every other searchable lookup shows — the
            report filter was the one bare search box. */}
        <span aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
          </svg>
        </span>
        <input
          type="text"
          value={query}
          aria-label={col.label}
          placeholder={multi ? 'Search to add…' : 'Search…'}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            setBrowsing(false)
            if (!multi && !e.target.value && selected.length > 0) {
              setSelected([])
              onChange('')
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false)
              setBrowsing(false)
            }
          }}
          /* Not `.input-rc`: the magnifier (left-2.5), the clear × and the
             chevron (right-2) are absolutely positioned over this box, and their
             only clearance is the pl-8 / pr-14 / pr-8 here. `.input-rc` is
             unlayered and sets `padding: 0 0.5rem`, which would put the typed
             query under the magnifier. Border + radius only. */
          className={`block w-full text-sm border border-rc-control rounded-rc-control pl-8 py-1.5 focus:ring-2 focus:ring-cb-accent/20 focus:border-rc-chip-text ${!multi && selected.length > 0 ? 'pr-14' : 'pr-8'}`}
        />
        {!multi && selected.length > 0 && (
          <button
            type="button"
            onClick={clearSingle}
            aria-label="Clear"
            className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {/* CC-49747: browse the full list without typing (V1 combo parity). */}
        <button
          type="button"
          onClick={() => {
            if (listOpen && browsing) {
              setOpen(false)
              setBrowsing(false)
            } else {
              setBrowsing(true)
              setOpen(true)
            }
          }}
          aria-label={`Show all ${col.label} options`}
          /*
           * Was aria-haspopup="listbox" while the panel it opens contains no
           * listbox and no options — only buttons. An ARIA declaration that the
           * DOM contradicts is worse than a generic one, because a screen reader
           * announces a listbox and then finds nothing to navigate. Either add
           * real option semantics WITH arrow-key navigation, or say "true"; this
           * says "true".
           */
          aria-haspopup="true"
          aria-expanded={listOpen && browsing}
          aria-controls={listOpen ? lookupListId : undefined}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {listOpen && (
        /* z-50: paint over the report grid's sticky header (z-20), which cut
           across the open list on Aged AP Detail (CC-49862). */
        <div
          id={lookupListId}
          role="group"
          aria-label={`${col.label} options`}
          className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {searchPending && <div className="px-3 py-2 text-xs text-gray-500">{browsing ? 'Loading…' : 'Searching…'}</div>}
          {/* "No matches" is now a claim only a COMPLETED search may make. */}
          {searchReady && items.length === 0 && <div className="px-3 py-2 text-xs text-gray-500">No matches</div>}
          {lookupState === 'blocked' && (
            /* CC-53132: react-query paused the fetch, so nothing was searched and
               nothing failed either — the one state that used to read as "no
               matches". Say which it is, and offer the retry in the popover the
               user is already looking at. */
            <div role="alert" className="border-b border-amber-100 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <p className="font-medium">Can’t reach the server, so we haven’t searched yet.</p>
              <p className="mt-0.5 text-amber-800">This is not “no matches” — check your connection.</p>
              <button
                type="button"
                onClick={() => lookupQuery.refetch()}
                className="mt-1 font-medium text-cb-accent-text underline hover:no-underline"
              >
                Try again
              </button>
            </div>
          )}
          {lookupState === 'failed' && (
            /* The sibling case, which had no branch at all: a rejected search fell
               through `items.length === 0` and also read as "No matches". */
            <div role="alert" className="px-3 py-2 text-xs text-red-600">
              <p>Couldn’t run this search — this is not “no matches”.</p>
              <button
                type="button"
                onClick={() => lookupQuery.refetch()}
                className="mt-1 font-medium text-cb-accent-text underline hover:no-underline"
              >
                Try again
              </button>
            </div>
          )}
          {items.map((item) => {
            // CC-49947: show already-picked values as disabled with an explicit
            // "Already selected" note rather than hiding them — the user can see
            // the value IS in the list and why clicking it does nothing.
            const already = multi && isSelected(item.id)
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => select(item)}
                disabled={already}
                aria-disabled={already || undefined}
                title={already ? 'Already selected' : undefined}
                className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${
                  already ? 'cursor-not-allowed text-gray-400' : 'hover:bg-gray-50'
                }`}
              >
                <span className="truncate">{item.name || '(no name)'}</span>
                {already && (
                  <span className="shrink-0 text-[11px] uppercase tracking-wide text-gray-500">
                    Already selected
                  </span>
                )}
              </button>
            )
          })}
          {browsing && searchReady && items.length >= 50 && (
            <div className="px-3 py-2 text-[11px] text-gray-400 border-t border-gray-100">
              Showing the first 50 — type to narrow the list.
            </div>
          )}
          {/* CC-50289: cb-api ranks a bounded candidate window, so a term matching
              more rows than that window is shown as a partial list. Say so — a
              silently-shortened list of clients reads as "we don't have that
              client". */}
          {!browsing && searchReady && result?.truncated && items.length > 0 && (
            <div className="px-3 py-2 text-[11px] text-gray-500 border-t border-gray-100">
              {result.total
                ? `Showing the closest ${items.length} of ${result.total} matches — keep typing to narrow.`
                : 'Showing the closest matches — keep typing to narrow.'}
            </div>
          )}
        </div>
      )}
      {/* CC-49862: selected-value chips sit BELOW the input so they never push
          the search box down / out of alignment with the other filter fields. */}
      {multi && selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {selected.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1 bg-cb-accent/10 text-cb-accent-text text-xs px-2 py-0.5 rounded">
              {s.name || s.id}
              <button type="button" onClick={() => remove(s.id)} aria-label={`Remove ${s.name || s.id}`} className="hover:text-red-600">
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const TagManagerFilter = (props) => <LookupFilter {...props} multi />

// CC-49900: specialized filter bars (Matter Metrics) reuse these — pass a
// minimal col ({ label, filterConditions: [{ key: '<typeId>' }] }), a stored
// "id:name|id2:name2" value, and an onChange. Named component exports keep
// react-refresh happy.
export { LookupFilter, TagManagerFilter, DropdownFilter }

// Spec 184 (CC-54222): a NUMBER filter is ONE number, exactly like V1's plain
// `<input type="number">` (Report.cshtml). CB reads the value with ParseInteger /
// ParseDecimal and nothing else, so an operator here was a promise CB could not
// keep — and every edit made through one ran the report with -1. The box shows
// the same `numberFilterValue` the wire sends, so a value stored by the old
// operator control reads as the number that was in its box.
const NumberFilter = ({ col, value, onChange, hideLabel }) => {
  const integer = col.dataType === 'INTEGER'
  return (
    <div>
      {fieldLabel(col, hideLabel)}
      <input
        type="number"
        inputMode={integer ? 'numeric' : 'decimal'}
        step={integer ? 1 : 'any'}
        value={numberFilterValue(value, col)}
        onChange={(e) => onChange(numberFilterValue(e.target.value, col))}
        aria-label={col.label}
        className="block w-full input-rc"
      />
    </div>
  )
}

const NumberPickerFilter = ({ col, value, onChange, hideLabel }) => {
  const parts = (value || '').split('|')
  // Spec 184: a stored value may name its operator by cb2 name or by CB's code (a
  // definition default is written the V1 way, "2|100"); the select shows either.
  const operator = numberPickerOperator(parts[0])?.name || 'IsEqualTo'
  const v1 = parts[1] || ''
  const v2 = parts[2] || ''
  const update = (op, val1, val2) => {
    const arr = [op]
    if (val1 !== '' || op === 'IsBetween') arr.push(val1)
    if (op === 'IsBetween') arr.push(val2)
    onChange(arr.join('|'))
  }
  return (
    <div>
      {fieldLabel(col, hideLabel)}
      <div className="flex items-center gap-2">
        <select
          value={operator}
          onChange={(e) => update(e.target.value, v1, v2)}
          aria-label={`${col.label} operator`}
          className="select-rc"
        >
          {NUMBER_PICKER_OPERATORS.map((o) => (
            <option key={o.name} value={o.name}>{o.label}</option>
          ))}
        </select>
        {operator !== 'IsEmpty' && operator !== 'IsNotEmpty' && (
          <input
            type="number"
            value={v1}
            onChange={(e) => update(operator, e.target.value, v2)}
            aria-label={`${col.label} value`}
            className="flex-1 min-w-0 input-rc"
          />
        )}
        {operator === 'IsBetween' && (
          <>
            <span className="text-xs text-gray-500">and</span>
            <input
              type="number"
              value={v2}
              onChange={(e) => update(operator, v1, e.target.value)}
              aria-label={`${col.label} second value`}
              className="flex-1 min-w-0 input-rc"
            />
          </>
        )}
      </div>
    </div>
  )
}

const FILTER_COMPONENTS = {
  TEXT: TextFilter,
  NUMBER: NumberFilter, // spec 184: one number, V1's plain input — no operator
  NUMBERPICKER: NumberPickerFilter,
  CHECKBOX: DropdownFilter,
  RADIO: RadioFilter, // CC-49861: single-select (legacy renders radio buttons)
  BOOL: BoolFilter,
  DROPDOWN: DropdownFilter,
  ACCOUNTSDROPDOWN: DropdownFilter,
  LOOKUP: LookupFilter,
  TAGMANAGER: TagManagerFilter,
  DATE: DateFilter,
  SIMPLEDATE: SimpleDateFilter,
}

/**
 * The report's filter fields, rendered from its column metadata.
 *
 * Exported so the Queue Report modal renders the SAME controls the report page
 * does instead of rebuilding them — one registry, one behaviour. Exported as a
 * COMPONENT (not the raw FILTER_COMPONENTS map) to keep this file clean under
 * react-refresh/only-export-components.
 *
 * The panel below renders through this too, with the same default field width,
 * so its output is unchanged.
 */
export const ReportFilterFields = ({ columns = [], values = {}, onChange, accountIds, fieldClassName = 'w-full sm:w-64', variant = 'panel' }) => (
  <>
    {columns.map((col) => {
      /*
       * Section E2 (Himaad 2026-08-24) — the ONE per-column decision, and the
       * reason it is one line: this component is the single place every filter
       * control in the product renders through, and BOTH chrome hosts go through
       * it (ReportView.jsx:1249 for the 119 generic reports, and
       * SpecializedFilterBar.jsx:183 for the 21 specialized). One edit reaches
       * 140/140.
       *
       * Computed ONCE and consumed TWICE — for the label and for the wrapper —
       * so the two cannot drift into a control with no visible label of any
       * kind, which is what a second `variant === 'pills'` test invites.
       */
      const isPill = variant === 'pills' && !INLINE_FILTER_TYPES.has(col.filterTypeName)
      const isInline = variant === 'pills' && !isPill
      const Comp = FILTER_COMPONENTS[col.filterTypeName] || TextFilter
      const control = (
        <Comp
          col={col}
          value={values[col.columnId]}
          onChange={(v) => onChange(col.columnId, v)}
          accountIds={accountIds}
          // spec 092: in pill mode the label lives on the pill's trigger, so the
          // control must not draw it again — two copies on one page make every
          // `getByText(label)` ambiguous, and it looks like a bug besides.
          //
          // E2 makes this MANDATORY rather than cosmetic. With the pill gone the
          // trigger is gone, and the trigger IS the accessible label
          // (FilterPill.jsx:41 triggerLabel, :48 renders {label}) — leaving
          // hideLabel true would ship an inline date with no visible label at
          // all. Keyed on `isPill`, so it can only be true where a pill exists.
          hideLabel={isPill}
          // Layout only; every wire value is identical in both modes.
          inline={isInline}
        />
      )

      // spec 092 — the mock's Bar 2: each filter collapses to a pill showing
      // "Label: Value", with the control itself behind it. One switch here reaches
      // all 140 reports, because both chrome hosts render through this component
      // (and so does the Queue Report modal, which stays on 'panel').
      if (isPill) {
        return (
          <FilterPill
            key={col.columnId}
            label={resolveFilterLabel(col, columns)}
            summary={filterValueSummary(col, values[col.columnId])}
            active={hasCriterion(values[col.columnId])}
          >
            {control}
          </FilterPill>
        )
      }

      /*
       * E2 — a date in the pills row, with no pill around it.
       *
       * It needs its OWN wrapper rather than falling through to the panel one
       * below: `fieldClassName` defaults to 'w-full sm:w-64', which inside the
       * pills row would render the date 256px wide and full-width on narrow — a
       * layout bug, not an inline control.
       *
       * CORRECTED for E2 (spec 117). This note used to claim the `items-end`
       * below "bottom-aligns the control under its label with the 38px pills
       * beside it", and cited the wrap row at ":1228". Both halves were wrong.
       * The line number had drifted (the pills row is the `flex flex-wrap` div
       * near the end of this file), and — the part that mattered — `items-end`
       * HERE cannot align anything with the pills, because it only orders this
       * wrapper's own children. The pills are siblings of the wrapper, in the
       * PARENT row, and that row was `items-center`; it is `items-end` now, which
       * is what actually does the aligning. This `items-end` is retained because
       * the wrapper can hold more than one child, but it is not load-bearing for
       * the alignment the old sentence promised.
       *
       * QueueReportModal.jsx:211 takes the variant='panel' default and must keep
       * hitting the fallthrough with its own w-full sm:w-[calc(50%-0.5rem)].
       */
      if (isInline) {
        return (
          <div key={col.columnId} className="flex items-end gap-2">
            {control}
          </div>
        )
      }

      return (
        <div key={col.columnId} className={fieldClassName}>
          {control}
        </div>
      )
    })}
  </>
)

// `accountIds` = the definition's filteredAccountIds — the per-report account
// subset CB computes for ACCOUNTSDROPDOWN filters (CC-49864/65, V1 parity).
// CC-50013 G2: `actions` renders page-owned controls (a Views menu, a mode
// select) inline with Reset/Run instead of forcing a specialized page to keep a
// second action row outside the panel. Optional and undefined on every generic
// report, so their layout is unchanged.
// `disabled` is deliberately SEPARATE from `isLoading`: a page that cannot run
// yet (an inverted date range, no budget chosen, an empty category selection) must
// show a disabled "Run report", not the word "Running…". Folding the two together
// is how a blocked page came to claim it was already running.
const ReportFilterPanel = ({ columns = [], values = {}, onChange, onRun, onReset, onCollapse, isLoading, disabled, enableDateBasis = false, accountIds, onRunBackground, onSchedule, actions, chips, hasRun = false, runLabel }) => {
  // The split Run/Queue control replaces the plain Run button ONLY where the
  // caller supplies a background-run handler (generic reports today). Everywhere
  // else this component renders exactly as before.
  /*
   * spec 092 — the run control has TWO states.
   *
   * Before a result exists, running the report IS the page's headline act, so it
   * is the primary CTA and says "Run report" — one primary per screen, and it is
   * the run action. Once there is a result on screen, re-running is no longer
   * headline: it becomes "Refresh" in the mock's neutral outlined treatment.
   *
   * That CTA is BLUE with white text as of 2026-08-26 (Himaad feedback, B1):
   * .btn-cb-primary is filled --color-rc-chip-text (#1d4ed8), the same blue as
   * the refresh chips, the focus rings and every .is-active state, and white on
   * it is 6.70:1. Radius is --radius-rc-control (6px), per B2.
   * SUPERSEDED, kept as history: this paragraph used to read "it is the orange
   * CTA" — navy text on brand orange #ff754a with the 17px --radius-cb pill.
   * Do not repaint it back; see the measurement table at src/index.css:170-200.
   *
   * Geometry does NOT change between the two (same padding, same 6px radius,
   * same split halves) — only colour and label — so the bar does not reflow at
   * the moment a report finishes.
   * SUPERSEDED, kept as history: that sentence used to end "...so the bar CANNOT
   * reflow", and until 2026-08-26 it was simply FALSE. .btn-cb-primary carried no
   * border while .btn-cb-neutral carried a 1px one, so under border-box the two
   * states measured 36px and 38px and the bar grew 2px the instant a report
   * finished. Nothing in the repo could see it — no unit test asserts computed
   * geometry, and neither e2e audit measures height. What actually holds the
   * geometry stable now is identical padding, identical font metrics, and a 1px
   * border on BOTH states (B3 equalizes all four .btn-cb-* variants at 38px). If
   * you change the border width on one state you MUST change the other. Full
   * account at src/index.css, the .btn-cb-neutral note.
   *
   * A caller's explicit `runLabel` still wins: several specialized pages name this
   * button after what it does on that page.
   */
  const label = isLoading ? 'Running…' : (runLabel || (hasRun ? 'Refresh' : 'Run report'))
  const RunControl = onRunBackground
    ? <RunQueueSplitButton onRun={onRun} onRunBackground={onRunBackground} onSchedule={onSchedule} isLoading={isLoading} disabled={disabled} label={label} neutral={hasRun && !isLoading} />
    : (
      <button
        data-pendo="report-run"
        onClick={onRun}
        disabled={isLoading || disabled}
        className={hasRun && !isLoading ? 'btn-cb-neutral' : 'btn-cb-primary'}
      >
        {label}
      </button>
    )

  const filterable = useFilterableColumns(columns)

  // CC-49326: 'by date' basis selector. When a report exposes MULTIPLE date-range
  // filters (e.g. invoice generated / issued / paid), let the user say which date
  // the range applies to: picking a basis moves the current range onto that
  // column and resets the other date filters to All Dates so only the chosen
  // basis constrains the report. Reports with 0–1 date filters are untouched.
  // Bug #9 (QA 7/2) gated the selector OFF; CC-49578 re-enables it via
  // reportFeatureConfig.js (dateBasisAuto: any report whose definition carries
  // 2+ filterable DATE columns). 0-1 date columns keep it hidden regardless.
  const dateCols = useMemo(
    () => (enableDateBasis ? filterable.filter((c) => c.filterTypeName === 'DATE') : []),
    [filterable, enableDateBasis]
  )
  const activeDateValue = (cid) => {
    const v = values[cid]
    return v && v !== ALL ? v : null
  }
  const basisId =
    dateCols.find((c) => activeDateValue(c.columnId))?.columnId ?? dateCols[0]?.columnId
  const onBasisChange = (nextIdRaw) => {
    const nextId = parseInt(nextIdRaw, 10)
    if (nextId === basisId) return
    const currentRange = basisId != null ? activeDateValue(basisId) : null
    for (const c of dateCols) {
      if (c.columnId === nextId) onChange(c.columnId, currentRange || values[c.columnId] || ALL)
      else onChange(c.columnId, ALL)
    }
  }

  if (filterable.length === 0) {
    return (
      <div data-print-hide className="bg-white rounded-rc-card border border-rc-line shadow-rc-card p-4 mb-4 flex items-center justify-between">
        <p className="text-xs text-gray-500">This report has no filters.</p>
        <div className="flex items-center gap-2">
          {actions}
          {RunControl}
        </div>
      </div>
    )
  }

  return (
    <div data-print-hide className="bg-white rounded-rc-card border border-rc-line shadow-rc-card p-4 mb-4">
      {/* spec 092 — the applied-criteria chips belong INSIDE the query card
          (Figma 110:8927), not in a second card floating beneath it. The host owns
          the chips because removability and label resolution are path-specific;
          the card only owns where they sit. */}
      {chips && (
        <div className="mb-3 border-b border-rc-line pb-3">{chips}</div>
      )}
      {onCollapse && (
        <div className="flex justify-end -mt-1 mb-1.5">
          {/* B6 (spec 117) deliberately does NOT touch this ▲. B6 swaps filled
              dropdown CARETS for the outline chevron; this is a DISCLOSURE
              control — it points up because the panel collapses upward, and there
              is no menu behind it. Swapping it for a chevron-down would have been
              the wrong glyph, and for a chevron-up would have imported a second
              icon for a control the mock does not cover. */}
          <button type="button" onClick={onCollapse} className="btn-cb-tertiary">
            Hide filters ▲
          </button>
        </div>
      )}
      {dateCols.length >= 2 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 pb-2.5 border-b border-gray-100">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider" htmlFor="report-date-basis">
            By date
          </label>
          <select
            id="report-date-basis"
            aria-label="Date basis"
            value={String(basisId ?? '')}
            onChange={(e) => onBasisChange(e.target.value)}
            /* Geometry and border only. Which date the range applies to, and how
               any date is rendered or sent, is untouched — date FORMATTING is
               Section C and explicitly out of this batch. */
            className="select-rc"
          >
            {dateCols.map((c) => (
              <option key={c.columnId} value={String(c.columnId)}>{c.label}</option>
            ))}
          </select>
          <span className="text-[11px] text-gray-400">
            The date range applies to this date; other date filters reset to All Dates.
          </span>
        </div>
      )}
      {/*
        spec 092 — Bar 2 (Figma 110:8927). Each filter is a PILL reading
        "Label: Value"; the control itself lives in the pill's popover. The row
        wraps, so a report with many filters grows the card rather than scrolling
        sideways — the mock's own note says pills wrap to a second line.

        `variant` is the whole switch, and it sits on ReportFilterFields, which is
        the single component every filter control in the product renders through.
        One change, all 140 reports, both chrome hosts. The Queue Report modal
        deliberately stays on 'panel': it is a form in a dialog, where a row of
        pills that each open another popover would be a popover inside a popover.

        E2 (Himaad 12:20 "do you see the as of date on the top, the positioning of
        it?" / Jordan "Oh yeah, it's wonky") — `items-end`, was `items-center`.

        The inline filters (a date, per INLINE_FILTER_TYPES) are the only children
        of this row that render a LABEL above their control, so they are 20.5px
        taller than every 38px pill beside them. Under `items-center` that extra
        height was split above and below, which pushed the pills 8.25px UP off the
        date field's baseline — the wonk. Measured against the real emitted CSS
        (Inter, html line-height 24px, label box 16.5px + mb-1):

          items-center  bottom-edge delta 8.25px   centre delta 10.25px
          items-end     bottom-edge delta 0.00px   centre delta  2.00px

        The residual 2px is not this row's to fix: it is half the 38px-vs-34px
        difference between a pill and the date input's own box, and equalising
        those needs Himaad (see spec 117 — the date box is deliberately NOT moved
        to 38px here, because `.input-rc-lg` would defeat the `pr-7` UsDateInput
        appends for calendar-icon clearance).

        ALSO MOVED BY THIS LINE, and not mentioned here until 2026-09-08: the
        `ml-auto` cluster below (Views / Reset filters / Run report, plus the
        export controls on the specialized pages) is a SIBLING flex item in this
        same row, so it bottom-aligns with the pills. On a report carrying an
        inline date the row's tallest child is 54.5px, so a 38px item's top
        offset goes 8.25px -> 16.5px and Run report — the page's primary CTA —
        drops 8.25px on those reports. It stays level with the pills, which is
        the outcome E2 wants, but nobody approving "one line for the as-of-date"
        was told they were approving it too. The review artifact is a screenshot
        of a report with an inline date (Aged A/R, id 21).

        Do NOT copy this line to the "By date" row above. The reason is scope,
        NOT geometry: an earlier version of this comment claimed that row's
        "content is 38px-only, so centre and end are the same pixel there",
        which is false — that row holds a 12px `text-xs` label and an 11px note
        that wraps at narrow widths alongside the 38px `.select-rc`, so
        `items-end` there would drop both text nodes to the select's baseline,
        ~11px for the label and up to ~19px for a two-line note. That text reads
        correctly centred against a 38px control, Himaad did not name the row,
        and nobody has measured an alternative. Leave it.
      */}
      <div className="flex flex-wrap items-end gap-2">
        <ReportFilterFields columns={filterable} values={values} onChange={onChange} accountIds={accountIds} variant="pills" />
        <div className="ml-auto flex items-center gap-2">
          {actions}
          {/*
            B1 (Himaad, 8/26) named Reset explicitly, and it is the reason: this
            was .btn-cb-ghost, whose 1.5px (now 1px) --color-cb-orange border is
            the ONLY decoration that class carries. With the primary CTA repainted
            blue, a ghost Reset would have been the single orange element left on
            the report page, sitting immediately beside the blue primary —
            hierarchy inverted, and it reads as a bug rather than as a style.

            .btn-rc is the right home: it is exactly what Views / Display View /
            Column Filter wear one bar up, so Reset now matches its actual peers —
            38px, 6px radius, and a --color-rc-control border at 3.03:1 (the ghost
            outline was orange #ff754a at 2.66:1, which never cleared the SC
            1.4.11 floor for a control border either).

            E4 renames it "Reset filters" (Himaad's feedback; the IA choice among
            three options was Jordan's, 2026-08-27 — specs/102 §6). Cited as "E1"
            in this comment until 2026-08-28, which was wrong in the way that
            costs a reader an hour: E1 is Himaad's "promote the top-N most-used
            filters inline", dispositioned ✗ NOT PLANNED in specs/102 §1, so the
            citation sent you to a table row saying this work does not exist.

            The complaint was that "Reset" did not say WHAT it resets, and on this
            page there are two
            different resets a click away from each other: this one restores the
            report's default filter VALUES, and the grid's "Reset grid layout"
            (inside Display View) restores the LAYOUT. The names now carry the
            scope, so neither has to be guessed from where it sits.

            Locators that find THIS button, re-counted 2026-08-27 against the tree
            (the previous count cited ReportFilterPanel.test.jsx:37/:91 and
            ReportView.test.jsx:316 — all three line numbers had already drifted,
            which is why they are re-stated as file:line here rather than trusted):
              - ReportFilterPanel.test.jsx:45, :99, :801  — /reset/i, still match
              - ReportView.test.jsx:359                   — exact 'Reset', BREAKS
              - specializedFilterBar.statements.test.jsx:199,
                specializedFilterBar.wire.test.jsx:145/:298/:318,
                specializedFilterBar.wire2.test.jsx:299   — /^reset$/i, all BREAK
            The ~8 other "Reset" hits in the suites are different buttons (the
            grid's "Reset grid layout", ReportBand's "Reset to defaults",
            ColumnAliasEditor's per-row Reset) and are NOT evidence about this one.

            ⚠ The three /reset/i matchers above pass only because the panel renders
            in ISOLATION in that suite. Inside ReportView this button and the
            saved-filters band's "Reset to defaults" both match /reset/i, and
            getByRole THROWS on two matches — so any future ReportView assertion
            must anchor, e.g. /^reset filters$/i.

            Rendered CONDITIONALLY, which is new. SpecializedFilterBar.jsx:188
            passes `onReset={defaults ? onReset : undefined}` — the contract
            deliberately supports a page with no defaults to reset to, and this
            button used to render for it anyway with onClick={undefined}: a
            control that looks live, takes focus, and does nothing. No shipping
            page hits it today (all 20 specialized pages pass
            `defaults={makeDefaults()}`, and ReportView always passes
            onResetFilters), so this changes nothing on screen now and closes the
            dead control the contract allows.
          */}
          {onReset && (
            <button onClick={onReset} className="btn-rc">
              Reset filters
            </button>
          )}
          {RunControl}
        </div>
      </div>
    </div>
  )
}

export default ReportFilterPanel
